"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CircleCheck as CheckCircle2, Star, ShieldCheck, MapPin, ExternalLink, Send, MessageSquare, X, ChevronLeft, ChevronRight, GraduationCap, CircleAlert as AlertCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "@/components/common/Link";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
type Language = "es" | "en" | "eu" | "fr";
const LANGUAGE_STORAGE_KEY = "wvc_language";
const LANGUAGE_EVENT_NAME = "wvc-language-change";
function useLanguage(): [Language, (lang: Language) => void] {
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      if (typeof window !== "undefined") {
        const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (saved && ["es", "en", "eu", "fr"].includes(saved)) {
          return saved as Language;
        }
      }
    } catch {
      // Ignore storage errors
    }
    return "es";
  });
  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      try {
        const customEvent = event as CustomEvent<Language>;
        if (customEvent.detail && ["es", "en", "eu", "fr"].includes(customEvent.detail)) {
          setLanguageState(customEvent.detail);
        }
      } catch {
        // Ignore event errors
      }
    };
    window.addEventListener(LANGUAGE_EVENT_NAME, handleLanguageChange);
    return () => {
      window.removeEventListener(LANGUAGE_EVENT_NAME, handleLanguageChange);
    };
  }, []);
  const setLanguage = (newLang: Language) => {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang);
      window.dispatchEvent(new CustomEvent(LANGUAGE_EVENT_NAME, {
        detail: newLang
      }));
    } catch {
      // Ignore storage errors
    }
    setLanguageState(newLang);
  };
  return [language, setLanguage];
}
const technicianImage = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/file_000000004b988243aa2cecc796989cc0.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/file_000000004b988243aa2cecc796989cc0.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/file_000000004b988243aa2cecc796989cc0.png-wvc-sizes",
  alt: "Técnico de Electro & Más con ropa de trabajo corporativa realizando una instalación eléctrica en un cuadro abierto durante una obra"
};
const loxoneImages = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260415_170148.jpg",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260415_170148.jpg-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260415_170148.jpg-wvc-sizes",
  alt: "Formación técnica oficial Loxone - configuración de sistemas de automatización"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260413_145713.jpg",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260413_145713.jpg-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260413_145713.jpg-wvc-sizes",
  alt: "Profesionales en sesión de formación técnica Loxone - capacitación en automatización"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260415_170156.jpg",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260415_170156.jpg-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/20260415_170156.jpg-wvc-sizes",
  alt: "Formación técnica con kit de producto Loxone - desarrollo de soluciones de automatización"
}];
const translations = {
  es: {
    badge: "Garantía & Calidad Bilbao y País Vasco",
    heading: "«Instalaciones seguras, clientes tranquilos. Tu electricidad en manos expertas.»",
    mission: "Somos una empresa especializada en instalaciones eléctricas, reformas, mantenimiento y reparación. Trabajamos para empresas, locales, comunidades y viviendas, ofreciendo soluciones adaptadas a cada instalación: desde pequeñas averías hasta reformas completas, cuadros eléctricos, iluminación, puntos de recarga, domótica y legalizaciones.",
    techRole: "Técnico especialista y fundador de Electro & Más",
    techStatus: "En activo",
    bio: "Como fundador y técnico especialista de Electro & Más, Carlos Leandro Claros Villa lidera cada proyecto con un compromiso directo de calidad, cercanía y máxima precisión técnica. Con una sólida trayectoria en instalaciones eléctricas y domótica en Bizkaia y en todo el País Vasco, garantizamos un servicio oficial, autorizado y transparente diseñado para la tranquilidad de cada cliente.",
    credentials: ["Empresa instaladora autorizada de electricidad en Bizkaia y en todo el País Vasco", "Especialistas certificados en domótica Loxone", "Seguro de responsabilidad civil de alta cobertura", "Presupuestos transparentes, sin sorpresas ni costes ocultos", "Técnicos homologados con formación continua en automatización"],
    loxoneTitle: "Formación oficial Loxone",
    loxoneText: "Nuestro equipo se capacita y certifica directamente con Loxone para garantizar instalaciones y programación de máxima calidad. Reforzamos nuestro compromiso como empresa autorizada con personal serio y bien preparado.",
    viewPhoto: "Ver foto",
    reviewsBadge: "Lo que dicen nuestros clientes",
    reviewsTitle: "Reseñas de verdad, trabajo de verdad",
    reviewsCount: "25 reseñas verificadas",
    workedWithUsTitle: "¿Has trabajado con nosotros?",
    workedWithUsText: "Tu opinión ayuda a otros vecinos de Bilbao y del País Vasco a decidirse.",
    successTitle: "¡Muchas gracias por tu opinión!",
    successText: "Valoramos enormemente tu confianza en Electro & Más.",
    ratingLabel: "Tu valoración",
    namePlaceholder: "Tu nombre (opcional)",
    experiencePlaceholder: "Escribe aquí tu experiencia sobre la instalación o servicio...",
    publishBtn: "Publicar reseña",
    privacyConsentCheckbox: "He leído y acepto la",
    privacyPolicyLink: "Política de Privacidad",
    publicationNotice: "Al enviar tu reseña, aceptas que tu nombre (si lo indicas) y el contenido de tu valoración puedan ser publicados en este sitio web.",
    privacyRequiredError: "Debes aceptar la Política de Privacidad para poder publicar tu reseña.",
    firstLayerInfo: {
      responsable: "Responsable: Carlos Leandro Claros Villa (Electro & Más)",
      finalidad: "Finalidad: gestionar y publicar tu reseña",
      legitimacion: "Legitimación: tu consentimiento",
      derechos: "Derechos: acceso, rectificación, supresión… escribiendo a electroymasbilbao@gmail.com",
      moreInfo: "Más información en la"
    },
    preferGoogle: "¿Prefieres valorarnos en Google?",
    googleReviewBtn: "Déjanos tu reseña en Google",
    mapsBtn: "Ver nuestra ficha en Google Maps"
  },
  en: {
    badge: "Warranty & Quality Bilbao and the Basque Country",
    heading: "Doing the job right. Ensuring client satisfaction.",
    mission: "We are a company specialized in electrical installations, renovations, maintenance, and repairs. We serve businesses, commercial premises, communities, and homes, providing tailored solutions for every project: from minor faults to complete renovations, electrical panels, lighting, EV chargers, home automation, and certifications.",
    techRole: "Specialist technician and founder of Electro & Más",
    techStatus: "Active",
    bio: "As founder and specialist technician of Electro & Más, Carlos Leandro Claros Villa leads every project with a direct commitment to quality, proximity, and utmost technical precision. With a proven track record in electrical installations and home automation in Biscay and across the Basque Country, we guarantee an official, authorized, and transparent service designed for complete peace of mind.",
    credentials: ["Authorized electrical installation company in Biscay and throughout the Basque Country", "Certified specialists in Loxone home automation", "High-coverage civil liability insurance", "Transparent quotes with no surprises or hidden costs", "Approved technicians with continuous training in automation"],
    loxoneTitle: "Official Loxone Training",
    loxoneText: "Our team trains and certifies directly with Loxone to ensure highest quality installations and programming. We reinforce our commitment as an authorized company with dedicated, well-prepared professionals.",
    viewPhoto: "View photo",
    reviewsBadge: "What our clients say",
    reviewsTitle: "Real reviews, real work",
    reviewsCount: "25 verified reviews",
    workedWithUsTitle: "Have you worked with us?",
    workedWithUsText: "Your feedback helps neighbors in Bilbao and the Basque Country make an informed choice.",
    successTitle: "Thank you very much for your feedback!",
    successText: "We truly value your trust in Electro & Más.",
    ratingLabel: "Your rating",
    namePlaceholder: "Your name (optional)",
    experiencePlaceholder: "Write your experience about the installation or service here...",
    publishBtn: "Submit review",
    privacyConsentCheckbox: "I have read and accept the",
    privacyPolicyLink: "Privacy Policy",
    publicationNotice: "By submitting your review, you agree that your name (if provided) and the text of your feedback may be published on this website.",
    privacyRequiredError: "You must accept the Privacy Policy to submit your review.",
    firstLayerInfo: {
      responsable: "Data Controller: Carlos Leandro Claros Villa (Electro & Más)",
      finalidad: "Purpose: manage and publish your review",
      legitimacion: "Legal basis: your consent",
      derechos: "Rights: access, rectification, erasure… by writing to electroymasbilbao@gmail.com",
      moreInfo: "More information in the"
    },
    preferGoogle: "Prefer to review us on Google?",
    googleReviewBtn: "Leave us a review on Google",
    mapsBtn: "View our Google Maps listing"
  },
  eu: {
    badge: "Bermea eta Kalitatea Bilbon eta Euskal Herrian",
    heading: "Lana ondo egitea. Bezeroa pozik geratzea.",
    mission: "Instalazio elektrikoetan, berrikuntzetan, mantentze-lanetan eta konponketetan espezializatutako enpresa gara. Enpresa, lokal, erkidego eta etxebizitzetarako lan egiten dugu, instalazio bakoitzera egokitutako konponbideak eskainiz: matxura txikietatik hasi eta eraberritze integraletara, koadro elektrikoak, argiztapena, karga-puntuak, domotika eta legeztatzeak.",
    techRole: "Teknikari espezialista eta Electro & Más-eko sortzailea",
    techStatus: "Aktiboan",
    bio: "Electro & Más-eko sortzaile eta teknikari espezialista gisa, Carlos Leandro Claros Villak proiektu bakoitza kalitate, hurbiltasun eta zehaztasun tekniko handienarekin gidatzen du. Bizkaian eta Euskal Herri osoan instalazio elektrikoetan eta domotikan esperientzia zabalarekin, bezero bakoitzaren lasaitasunerako diseinatutako zerbitzu ofizial, baimendu eta gardena bermatzen dugu.",
    credentials: ["Argindarraren instalatzaile-enpresa baimendua Bizkaian eta Euskal Herri osoan", "Loxone domotikan espezialista ziurtatuak", "Estaldura handiko erantzukizun zibileko asegurua", "Aurrekontu gardenak, ustekaberik eta ezkutuko kosturik gabe", "Automatizazioan etengabeko prestakuntza duten teknikari homologatuak"],
    loxoneTitle: "Loxone prestakuntza ofiziala",
    loxoneText: "Gure lantaldeak zuzenean Loxonerekin ikasten eta ziurtatzen du, kalitate goreneko instalazioak eta programazioa bermatzeko. Enpresa baimendu gisa dugun konpromisoa sendotzen dugu langile arduratsu eta trebatuen bidez.",
    viewPhoto: "Argazkia ikusi",
    reviewsBadge: "Gure bezeroek diotena",
    reviewsTitle: "Benetako iritziak, benetako lana",
    reviewsCount: "Egiaztatutako 25 iritzi",
    workedWithUsTitle: "Gurekin lan egin duzu?",
    workedWithUsText: "Zure iritziak Bilboko eta Euskal Herriko beste bizilagun batzuei laguntzen die erabakitzen.",
    successTitle: "Mila esker zure iritziagatik!",
    successText: "Asko baloratzen dugu Electro & Más-engan duzun konfiantza.",
    ratingLabel: "Zure balorazioa",
    namePlaceholder: "Zure izena (aukerakoa)",
    experiencePlaceholder: "Idatzi hemen instalazioari edo zerbitzuari buruzko zure esperientzia...",
    publishBtn: "Iritzia argitaratu",
    privacyConsentCheckbox: "Irakurri dut eta onartzen dut",
    privacyPolicyLink: "Pribatutasun Politika",
    publicationNotice: "Zure iritzia bidaltzean, onartzen duzu zure izena (adierazten baduzu) eta balorazioaren edukia webgune honetan argitaratu ahal izatea.",
    privacyRequiredError: "Pribatutasun Politika onartu behar duzu zure iritzia argitaratzeko.",
    firstLayerInfo: {
      responsable: "Arduraduna: Carlos Leandro Claros Villa (Electro & Más)",
      finalidad: "Helburua: zure iritzia kudeatzea eta argitaratzea",
      legitimacion: "Legitimazioa: zure baimena",
      derechos: "Eskubideak: eskuratzeko, zuzentzeko, ezabatzeko… electroymasbilbao@gmail.com helbidera idatziz",
      moreInfo: "Informazio gehiago hemen:"
    },
    preferGoogle: "Nahiago duzu Googlen baloratu?",
    googleReviewBtn: "Utzi zure iritzia Googlen",
    mapsBtn: "Ikusi gure fitxa Google Maps-en"
  },
  fr: {
    badge: "Garantie & Qualité Bilbao et Pays Basque",
    heading: "Faire du bon travail. Que le client soit satisfait.",
    mission: "Nous sommes une entreprise spécialisée dans les installations électriques, les rénovations, la maintenance et les réparations. Nous intervenons pour les entreprises, locaux commerciaux, copropriétés et logements, en offrant des solutions adaptées à chaque installation : des petites pannes aux rénovations complètes, tableaux électriques, éclairage, bornes de recharge, domotique et certifications officielles.",
    techRole: "Technicien spécialiste et fondateur d'Electro & Más",
    techStatus: "En activité",
    bio: "En tant que fondateur et technicien spécialiste d'Electro & Más, Carlos Leandro Claros Villa dirige chaque projet avec un engagement direct de qualité, de proximité et de rigueur technique. Fort d'une solide expérience dans les installations électriques et la domotique en Biscaye et dans tout le Pays Basque, nous garantissons un service officiel, agréé et transparent pour votre totale tranquillité.",
    credentials: ["Entreprise installatrice d'électricité agréée en Biscaye et dans tout le Pays Basque", "Spécialistes certifiés en domotique Loxone", "Assurance responsabilité civile à haute couverture", "Devis transparents, sans surprises ni frais cachés", "Techniciens homologués avec formation continue en automatisation"],
    loxoneTitle: "Formation officielle Loxone",
    loxoneText: "Notre équipe se forme et se certifie directement auprès de Loxone pour garantir des installations et une programmation de la plus haute qualité. Nous renforçons notre engagement d'entreprise agréée avec un personnel sérieux et qualifié.",
    viewPhoto: "Voir photo",
    reviewsBadge: "Ce que disent nos clients",
    reviewsTitle: "De vrais avis, du vrai travail",
    reviewsCount: "25 avis vérifiés",
    workedWithUsTitle: "Avez-vous travaillé avec nous ?",
    workedWithUsText: "Votre avis aide d'autres voisins de Bilbao et du Pays Basque à faire leur choix.",
    successTitle: "Merci beaucoup pour votre avis !",
    successText: "Nous apprécions énormément votre confiance en Electro & Más.",
    ratingLabel: "Votre évaluation",
    namePlaceholder: "Votre nom (optionnel)",
    experiencePlaceholder: "Écrivez ici votre expérience sur l'installation ou le service...",
    publishBtn: "Publier l'avis",
    privacyConsentCheckbox: "J'ai lu et j'accepte la",
    privacyPolicyLink: "Politique de Confidentialité",
    publicationNotice: "En soumettant votre avis, vous acceptez que votre nom (si renseigné) et le texte de votre évaluation puissent être publiés sur ce site web.",
    privacyRequiredError: "Vous devez accepter la Politique de Confidentialité pour publier votre avis.",
    firstLayerInfo: {
      responsable: "Responsable : Carlos Leandro Claros Villa (Electro & Más)",
      finalidad: "Finalité : gérer et publier votre avis",
      legitimacion: "Base légale : votre consentement",
      derechos: "Droits : accès, rectification, suppression… en écrivant à electroymasbilbao@gmail.com",
      moreInfo: "Plus d'informations dans la"
    },
    preferGoogle: "Vous préférez nous évaluer sur Google ?",
    googleReviewBtn: "Laissez-nous un avis sur Google",
    mapsBtn: "Voir notre fiche sur Google Maps"
  }
};
export default function Satisfaccion() {
  const [language] = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.es;
  const [rating, setRating] = useState<number>(5);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [reviewText, setReviewText] = useState<string>("");
  const [reviewerName, setReviewerName] = useState<string>("");
  const [privacyAccepted, setPrivacyAccepted] = useState<boolean>(false);
  const [privacyError, setPrivacyError] = useState<boolean>(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
  const [lightboxImages, setLightboxImages] = useState<Array<{
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  }> | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState<number>(0);

  // WVC Forms integration state
  const [formDef, setFormDef] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [hpValue, setHpValue] = useState<string>("");
  useEffect(() => {
    const client = getWvcClient();
    if (!client?.pluginRequest) return;
    client.pluginRequest("wvc-forms/v1/forms/resena-cliente").then((data: any) => {
      setFormDef(data);
    }).catch((err: any) => {
      if (err?.status === 404) {
        client.pluginRequest("wvc-forms/v1/forms").then((listRes: any) => {
          const matched = listRes?.items?.find((item: any) => item.key === "resena-cliente") || (listRes?.items?.length === 1 ? listRes.items[0] : null) || listRes?.items?.find((item: any) => typeof item?.name === "string" && item.name.toLowerCase().includes("reseña"));
          if (matched?.key) {
            return client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
          }
        }).then((matchedDef: any) => {
          if (matchedDef) setFormDef(matchedDef);
        }).catch(() => {});
      }
    });
  }, []);
  useEffect(() => {
    if (!isLightboxOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsLightboxOpen(false);
      } else if (e.key === "ArrowLeft" && lightboxImages && lightboxImages.length > 1) {
        setActiveImageIndex(prev => (prev - 1 + lightboxImages.length) % lightboxImages.length);
      } else if (e.key === "ArrowRight" && lightboxImages && lightboxImages.length > 1) {
        setActiveImageIndex(prev => (prev + 1) % lightboxImages.length);
      }
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isLightboxOpen, lightboxImages]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reviewText.trim()) return;
    if (!privacyAccepted) {
      setPrivacyError(true);
      return;
    }
    setPrivacyError(false);
    setSubmitError(null);
    setIsSubmitting(true);
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setSubmitError(language === "en" ? "Form service unavailable at the moment. Please try again later." : "El servicio de formularios no está disponible en este momento.");
      setIsSubmitting(false);
      return;
    }
    try {
      const calificacionField = formDef?.fields?.find((f: any) => f.key === "calificacion");
      const nombreField = formDef?.fields?.find((f: any) => f.key === "nombre");
      const experienciaField = formDef?.fields?.find((f: any) => f.key === "experiencia");
      const califKey = calificacionField ? calificacionField.id : "calificacion";
      const nomKey = nombreField ? nombreField.id : "nombre";
      const expKey = experienciaField ? experienciaField.id : "experiencia";
      const values: Record<string, any> = {
        [califKey]: String(rating),
        [expKey]: reviewText.trim()
      };
      if (reviewerName.trim()) {
        values[nomKey] = reviewerName.trim();
      }
      const formKey = formDef?.key || "resena-cliente";
      const payload: any = {
        values,
        token: formDef?.spam?.token || "",
        meta: {
          source_url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : ""
        }
      };
      if (formDef?.spam?.honeypot?.field) {
        payload.hp = {
          [formDef.spam.honeypot.field]: hpValue
        };
      }
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formKey}/submit`, {
        method: "POST",
        data: payload
      });
      if (res?.success) {
        setSubmitted(true);
        setPrivacyAccepted(false);
        setPrivacyError(false);
        setReviewText("");
        setReviewerName("");
      } else if (res?.code === "frms_validation_failed" && res?.errors) {
        const firstErrKey = Object.keys(res.errors)[0];
        setSubmitError(res.errors[firstErrKey]?.message || res?.message || "Por favor, revisa los datos introducidos.");
      } else {
        setSubmitError(res?.message || "No se ha podido enviar la reseña. Por favor, inténtalo de nuevo.");
      }
    } catch (err: any) {
      setSubmitError(err?.message || "Ocurrió un error al enviar tu reseña. Por favor, inténtalo de nuevo.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const openLightbox = (images: Array<{
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  }>, index: number = 0) => {
    setLightboxImages(images);
    setActiveImageIndex(index);
    setIsLightboxOpen(true);
  };
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const currentImage = lightboxImages ? lightboxImages[activeImageIndex] : null;
  const hasMultipleImages = Boolean(lightboxImages && lightboxImages.length > 1);
  return <section id="hacer-el-trabajo-bien-que-el-cliente-quede-satisfecho" data-nav="dark" className="relative overflow-hidden bg-background py-16 text-foreground md:py-24 lg:py-32">
      {/* Decorative hairline grid background effect */}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20" aria-hidden="true" />

      <div className="mx-auto max-w-[1904px] px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[1280px]">
          <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-12 lg:gap-16">
            
            {/* Left Column: Mission, Image, Verified Credentials & Loxone Training Gallery */}
            <motion.div className="space-y-8 lg:col-span-6" initial={{
            opacity: 0,
            x: -24
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            ease: "easeOut"
          }}>
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-secondary/80 px-3 py-1 font-mono text-xs font-semibold uppercase tracking-widest text-primary">
                  <ShieldCheck className="size-4" />
                  {t.badge}
                </div>
                <h2 className="font-sans text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                  {t.heading}
                </h2>
                <p className="font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
                  {t.mission}
                </p>
              </div>

              {/* Technician Image */}
              <div className="relative overflow-hidden rounded-xl border border-border/80 bg-card shadow-2xl">
                <img src={technicianImage.src} data-wvc-srcset={technicianImage.srcset} data-wvc-sizes={technicianImage.sizes} alt={technicianImage.alt} loading="lazy" onClick={() => openLightbox([technicianImage], 0)} className="h-72 w-full object-cover object-center sm:h-80 md:h-96 cursor-pointer transition-transform duration-300 hover:scale-[1.01]" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent opacity-90" />
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-lg border border-border/60 bg-card/90 p-3 backdrop-blur-md">
                  <div>
                    <p className="font-sans text-sm font-semibold text-foreground">Carlos Leandro Claros Villa</p>
                    <p className="font-mono text-xs text-muted-foreground">{t.techRole}</p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2.5 py-1 font-mono text-xs font-medium text-primary border border-primary/30">
                    <span className="size-2 rounded-full bg-primary animate-pulse" />
                    {t.techStatus}
                  </span>
                </div>
              </div>

              {/* Introduction Paragraph */}
              <p className="font-sans text-sm leading-relaxed text-muted-foreground sm:text-base">
                {t.bio}
              </p>

              {/* Bulleted Guarantees List */}
              <motion.ul variants={containerVariants} initial="hidden" whileInView="show" viewport={{
              once: true
            }} className="space-y-3.5 pt-2">
                {t.credentials.map((item, i) => <motion.li key={i} data-index={i} variants={itemVariants} className="flex items-start gap-3 rounded-lg border border-border/40 bg-card/50 p-3 transition-colors hover:border-primary/40">
                    <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-primary" />
                    <span className="font-sans text-sm font-medium text-foreground/90 sm:text-base">
                      {item}
                    </span>
                  </motion.li>)}
              </motion.ul>

              {/* Formación Oficial Loxone Gallery Block */}
              <div className="space-y-4 pt-4 border-t border-border/60">
                <div className="space-y-1.5">
                  <h3 className="font-sans text-lg font-bold tracking-tight text-foreground sm:text-xl flex items-center gap-2">
                    <GraduationCap className="size-5 text-primary" />
                    {t.loxoneTitle}
                  </h3>
                  <p className="font-sans text-sm leading-relaxed text-muted-foreground">
                    {t.loxoneText}
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                  {loxoneImages.map((image, i) => <div key={i} data-index={i} onClick={() => openLightbox(loxoneImages, i)} className="group relative cursor-pointer overflow-hidden rounded-xl border border-border/80 bg-card shadow-md transition-all duration-300 hover:border-primary/50 hover:shadow-lg">
                      <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} loading="lazy" className="h-36 w-full object-cover object-center transition-transform duration-300 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex items-end p-2.5">
                        <span className="font-mono text-xs font-semibold text-primary bg-background/90 px-2 py-1 rounded border border-primary/30">
                          {t.viewPhoto}
                        </span>
                      </div>
                    </div>)}
                </div>
              </div>
            </motion.div>

            {/* Right Column: Reviews & Interactive Feedback Form */}
            <motion.div className="space-y-6 lg:col-span-6" initial={{
            opacity: 0,
            x: 24
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            ease: "easeOut"
          }}>
              <Card className="border border-border/80 bg-card shadow-xl">
                <CardHeader className="space-y-3 pb-4">
                  <div className="flex items-center gap-2 text-xs font-mono font-semibold uppercase tracking-wider text-primary">
                    <MessageSquare className="size-4" />
                    {t.reviewsBadge}
                  </div>
                  <CardTitle className="font-sans text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                    {t.reviewsTitle}
                  </CardTitle>

                  {/* Rating Badge Header */}
                  <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border/60 bg-muted/40 p-4">
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-mono text-4xl font-extrabold text-foreground">5,0</span>
                      <span className="font-mono text-sm text-muted-foreground">/ 5</span>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1 text-primary">
                        {[...Array(5)].map((_, i) => <Star key={i} className="size-5 fill-primary text-primary" />)}
                      </div>
                      <p className="font-mono text-xs font-semibold text-muted-foreground">
                        {t.reviewsCount}
                      </p>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6">
                  {/* Feedback Prompt & Form */}
                  <div className="rounded-xl border border-border/60 bg-background/50 p-5 space-y-4">
                    <div>
                      <h3 className="font-sans text-lg font-bold text-foreground">
                        {t.workedWithUsTitle}
                      </h3>
                      <p className="font-sans text-sm text-muted-foreground mt-1">
                        {t.workedWithUsText}
                      </p>
                    </div>

                    {submitted ? <div className="rounded-lg border border-primary/40 bg-primary/10 p-4 text-center space-y-2">
                        <CheckCircle2 className="mx-auto size-8 text-primary" />
                        <h4 className="font-sans text-base font-bold text-foreground">
                          {t.successTitle}
                        </h4>
                        <p className="font-sans text-sm text-muted-foreground">
                          {t.successText}
                        </p>
                      </div> : <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Honeypot field for bot trap */}
                        {formDef?.spam?.honeypot && <input type="text" name={formDef.spam.honeypot.field} value={hpValue} onChange={e => setHpValue(e.target.value)} style={{
                      position: "absolute",
                      left: "-9999px"
                    }} tabIndex={-1} autoComplete="off" aria-hidden="true" />}

                        {/* Interactive Star Rating */}
                        <div className="space-y-2">
                          <label className="font-sans text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            {t.ratingLabel}
                          </label>
                          <div className="flex items-center gap-2">
                            {[1, 2, 3, 4, 5].map(star => <Button key={star} type="button" variant="ghost" onClick={() => setRating(star)} onMouseEnter={() => setHoverRating(star)} onMouseLeave={() => setHoverRating(0)} className="p-1 h-auto hover:bg-transparent focus:outline-none transition-transform hover:scale-110" aria-label={`Valorar con ${star} estrellas`}>
                                <Star className={`size-7 transition-colors ${star <= (hoverRating || rating) ? "fill-primary text-primary" : "text-muted-foreground/40"}`} />
                              </Button>)}
                            <span className="font-mono text-sm font-medium text-foreground ml-2">
                              {hoverRating || rating},0
                            </span>
                          </div>
                        </div>

                        {/* Name Input */}
                        <div className="space-y-1.5">
                          <Input type="text" placeholder={t.namePlaceholder} value={reviewerName} onChange={e => setReviewerName(e.target.value)} className="bg-card border-border/80 text-foreground placeholder:text-muted-foreground focus-visible:ring-primary" />
                        </div>

                        {/* Comment Textarea */}
                        <div className="space-y-1.5">
                          <Textarea placeholder={t.experiencePlaceholder} value={reviewText} onChange={e => setReviewText(e.target.value)} required rows={3} className="bg-card border-border/80 text-foreground placeholder:text-muted-foreground focus-visible:ring-primary" />
                        </div>

                        {/* RGPD Privacy Consent Checkbox & Publication Notice */}
                        <div className="space-y-3 pt-2">
                          <div className="flex items-start gap-2.5">
                            <Checkbox id="review-privacy-consent" checked={privacyAccepted} onCheckedChange={checked => {
                          const isChecked = checked === true;
                          setPrivacyAccepted(isChecked);
                          if (isChecked) {
                            setPrivacyError(false);
                          }
                        }} aria-required="true" aria-invalid={privacyError ? "true" : "false"} aria-describedby={privacyError ? "privacy-consent-error" : undefined} className="mt-0.5 border-border/80 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground focus-visible:ring-primary" />
                            <div className="space-y-1">
                              <label htmlFor="review-privacy-consent" className="font-sans text-xs sm:text-sm leading-snug text-foreground/90 cursor-pointer block">
                                {t.privacyConsentCheckbox}{" "}
                                <Link to="/PrivacyPolicyPage" newTab className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">
                                  {t.privacyPolicyLink}
                                </Link>
                                <span className="text-destructive ml-1" aria-hidden="true">*</span>
                              </label>
                              <p className="font-sans text-xs leading-relaxed text-muted-foreground">
                                {t.publicationNotice}
                              </p>
                            </div>
                          </div>

                          {privacyError && <div id="privacy-consent-error" role="alert" aria-live="assertive" className="flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/10 p-2.5 text-xs text-destructive">
                              <AlertCircle className="size-4 shrink-0 mt-0.5" />
                              <span>{t.privacyRequiredError}</span>
                            </div>}

                          {/* First-layer RGPD Informative Text */}
                          <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-[11px] leading-relaxed text-muted-foreground space-y-1">
                            <p>
                              <span className="font-medium text-foreground/80">{t.firstLayerInfo.responsable}</span> ·{" "}
                              <span>{t.firstLayerInfo.finalidad}</span> ·{" "}
                              <span>{t.firstLayerInfo.legitimacion}</span> ·{" "}
                              <span>{t.firstLayerInfo.derechos}</span> ·{" "}
                              <span>
                                {t.firstLayerInfo.moreInfo}{" "}
                                <Link to="/PrivacyPolicyPage" newTab className="text-primary underline underline-offset-2 hover:text-primary/80">
                                  {t.privacyPolicyLink}
                                </Link>
                                .
                              </span>
                            </p>
                          </div>
                        </div>

                        {/* Inline Error Notice */}
                        {submitError && <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
                            <AlertCircle className="size-4 shrink-0 mt-0.5" />
                            <span>{submitError}</span>
                          </div>}

                        {/* Submit Button */}
                        <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-all shadow-md shadow-primary/10 h-11 rounded-full disabled:opacity-60 cursor-pointer">
                          <Send className="size-4 mr-2" />
                          {isSubmitting ? language === "en" ? "Submitting..." : language === "eu" ? "Bidaltzen..." : language === "fr" ? "Envoi en cours..." : "Enviando..." : t.publishBtn}
                        </Button>
                      </form>}
                  </div>

                  {/* Google Reviews Callout Block */}
                  <div className="border-t border-border/60 pt-5 space-y-3">
                    <p className="font-sans text-sm font-semibold text-foreground">
                      {t.preferGoogle}
                    </p>
                    <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                      <Button variant="secondary" asChild className="w-full justify-start border border-border/80 bg-secondary text-secondary-foreground hover:bg-secondary/80 text-xs font-semibold h-10 rounded-full">
                        <Link to="https://share.google/to4UQ4FLrJOoo3VDH" newTab className="inline-flex items-center gap-2 truncate">
                          <Star className="size-3.5 shrink-0 text-primary" />
                          <span className="truncate">{t.googleReviewBtn}</span>
                        </Link>
                      </Button>

                      <Button variant="secondary" asChild className="w-full justify-start border border-border/80 bg-secondary text-secondary-foreground hover:bg-secondary/80 text-xs font-semibold h-10 rounded-full">
                        <Link to="https://share.google/vKdgvYjHZ5si8XAu4" newTab className="inline-flex items-center gap-2 truncate">
                          <MapPin className="size-3.5 shrink-0 text-primary" />
                          <span className="truncate">{t.mapsBtn}</span>
                          <ExternalLink className="size-3 shrink-0 ml-auto opacity-60" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {isLightboxOpen && currentImage && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: "easeOut"
      }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 sm:p-8" onClick={() => setIsLightboxOpen(false)} role="dialog" aria-modal="true" aria-label={currentImage.alt}>
            {/* Close Button */}
            <Button type="button" variant="ghost" size="icon" onClick={() => setIsLightboxOpen(false)} className="absolute top-4 right-4 z-[101] size-10 rounded-full bg-black/50 text-white hover:bg-white/20 hover:text-white" aria-label="Cerrar imagen">
              <X className="size-6" />
            </Button>

            {/* Navigation controls if multiple images exist */}
            {hasMultipleImages && <>
                <Button type="button" variant="ghost" size="icon" onClick={e => {
            e.stopPropagation();
            setActiveImageIndex(prev => (prev - 1 + (lightboxImages?.length || 1)) % (lightboxImages?.length || 1));
          }} className="absolute left-4 z-[101] size-11 rounded-full bg-black/50 text-white hover:bg-white/20 hover:text-white" aria-label="Imagen anterior">
                  <ChevronLeft className="size-6" />
                </Button>

                <Button type="button" variant="ghost" size="icon" onClick={e => {
            e.stopPropagation();
            setActiveImageIndex(prev => (prev + 1) % (lightboxImages?.length || 1));
          }} className="absolute right-4 z-[101] size-11 rounded-full bg-black/50 text-white hover:bg-white/20 hover:text-white" aria-label="Siguiente imagen">
                  <ChevronRight className="size-6" />
                </Button>

                {/* Counter Badge */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[101] rounded-full bg-black/60 px-3 py-1 font-mono text-xs text-white border border-white/10">
                  {activeImageIndex + 1} / {lightboxImages?.length}
                </div>
              </>}

            <motion.div key={currentImage.src} initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.2,
          ease: "easeOut"
        }} className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-lg flex items-center justify-center" onClick={e => e.stopPropagation()}>
              <img src={currentImage.src} data-wvc-srcset={currentImage.srcset} data-wvc-sizes={currentImage.sizes} alt={currentImage.alt} className="max-h-[85vh] max-w-[90vw] object-contain" />
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}